import { Asset } from "expo-asset";

// Import placeholders directly to get the raw module references (not ImageSourcePropType)
import music_placeholder_1 from "@assets/placeholder/music_placeholder_1.webp";
import music_placeholder_2 from "@assets/placeholder/music_placeholder_2.webp";
import music_placeholder_3 from "@assets/placeholder/music_placeholder_3.webp";
import music_placeholder_4 from "@assets/placeholder/music_placeholder_4.webp";
import music_placeholder_5 from "@assets/placeholder/music_placeholder_5.webp";
import music_placeholder_6 from "@assets/placeholder/music_placeholder_6.webp";
import music_placeholder_7 from "@assets/placeholder/music_placeholder_7.webp";

const placeholderModules = [
  music_placeholder_1,
  music_placeholder_2,
  music_placeholder_3,
  music_placeholder_4,
  music_placeholder_5,
  music_placeholder_6,
  music_placeholder_7,
];

// Cache: maps module index to localUri
const cachedPlaceholders: Map<number, string> = new Map();

// Track in-flight loads to avoid duplicate requests
const loadingPromises: Map<number, Promise<string>> = new Map();

// Eagerly start loading all placeholders when module is imported
// By the time user actually plays a song, these should be ready
let eagerLoadStarted = false;
function startEagerLoad() {
  if (eagerLoadStarted) return;
  eagerLoadStarted = true;

  // Load all in parallel, don't await - fire and forget
  Promise.all(
    placeholderModules.map((_, index) => loadPlaceholder(index))
  ).catch(() => {
    // Ignore errors - individual loads handle their own errors
  });
}

// Start loading immediately on module import
startEagerLoad();

/**
 * Loads a single placeholder on-demand and caches it.
 * Fast since it's loading from bundle.
 * Deduplicates concurrent requests for the same index.
 */
async function loadPlaceholder(index: number): Promise<string> {
  // Return cached value if available
  const cached = cachedPlaceholders.get(index);
  if (cached) return cached;

  // If already loading this index, wait for that promise
  const existing = loadingPromises.get(index);
  if (existing) return existing;

  // Start loading
  const loadPromise = (async () => {
    try {
      const asset = Asset.fromModule(placeholderModules[index]);
      await asset.downloadAsync();

      const uri = asset.localUri ?? "";
      if (uri) {
        cachedPlaceholders.set(index, uri);
      }
      return uri;
    } finally {
      loadingPromises.delete(index);
    }
  })();

  loadingPromises.set(index, loadPromise);
  return loadPromise;
}

/**
 * Computes deterministic index based on seed.
 */
function getPlaceholderIndex(seed?: string): number {
  const hash = seed
    ? seed.split("").reduce((acc, c) => acc + c.charCodeAt(0), 0)
    : 0;
  return Math.abs(hash) % placeholderModules.length;
}

/**
 * Gets a fallback artwork path for use with AudioPro.
 * Loads on-demand from bundle (fast) and caches the result.
 * Uses deterministic selection based on seed (e.g., song ID) for consistency.
 *
 * @param seed - Optional seed for deterministic selection (e.g., song ID)
 * @returns Local file path to placeholder
 */
export async function getFallbackArtwork(seed?: string): Promise<string> {
  const index = getPlaceholderIndex(seed);
  return loadPlaceholder(index);
}
